/**
 * Toast Compatibility Layer
 * 
 * This file provides a backward-compatible API that wraps Sonner,
 * allowing existing code using the Radix Toast API to work seamlessly
 * with Sonner notifications.
 * 
 * Migration: Radix UI Toast → Sonner
 * - Default variant → toast.success() with check icon ✓
 * - Destructive variant → toast.error() with error icon
 * - All other functionality mapped to Sonner equivalents
 */

import * as React from "react";
import { toast as sonnerToast } from "sonner";

// ============================================================================
// TYPE DEFINITIONS (Backward Compatible with Radix Toast)
// ============================================================================

export interface ToastOptions {
  title?: React.ReactNode;
  description?: React.ReactNode;
  variant?: 'default' | 'destructive';
  action?: React.ReactNode;
  duration?: number;
}

export interface ToastReturn {
  id: string | number;
  dismiss: () => void;
  update: (options: ToastOptions) => void;
}

// ============================================================================
// TOAST FUNCTION (Main API)
// ============================================================================

/**
 * Display a toast notification using Sonner
 * 
 * @param options - Toast configuration options
 * @returns Toast control object with id, dismiss, and update functions
 * 
 * @example
 * // Success toast with check icon
 * toast({ title: "Success", description: "Operation completed" });
 * 
 * @example
 * // Error toast
 * toast({ title: "Error", description: "Something went wrong", variant: "destructive" });
 */
export function toast(options: ToastOptions): ToastReturn {
  try {
    const { title, description, variant = 'default', action, duration } = options;
    
    // Warn about unsupported action prop in development
    if (action && process.env.NODE_ENV === 'development') {
      console.warn(
        '[Toast Migration] Action buttons are not supported in the Sonner migration. ' +
        'The toast will display without the action button.'
      );
    }
    
    // Prepare Sonner options
    const sonnerOptions: any = {
      description,
      duration,
    };
    
    // Map variant to appropriate Sonner toast type
    let toastId: string | number;
    
    if (variant === 'destructive') {
      // Destructive → Error toast with error icon
      toastId = sonnerToast.error(title, sonnerOptions);
    } else {
      // Default → Success toast with check icon ✓
      toastId = sonnerToast.success(title, sonnerOptions);
    }
    
    // Return control object
    return {
      id: toastId,
      dismiss: () => sonnerToast.dismiss(toastId),
      update: (newOptions: ToastOptions) => {
        // Sonner doesn't support direct updates
        // Dismiss old toast and create new one
        sonnerToast.dismiss(toastId);
        toast(newOptions);
      }
    };
    
  } catch (error) {
    // Fallback error handling
    console.error('[Toast Error]', error);
    
    // Show basic fallback notification
    const fallbackId = sonnerToast('Notification', {
      description: 'An error occurred while displaying the notification'
    });
    
    return {
      id: fallbackId,
      dismiss: () => sonnerToast.dismiss(fallbackId),
      update: () => {}
    };
  }
}

// ============================================================================
// USE TOAST HOOK (Backward Compatible)
// ============================================================================

/**
 * Hook that provides toast functionality
 * 
 * This hook maintains backward compatibility with the Radix Toast API
 * while internally using Sonner for all notifications.
 * 
 * @returns Object with toast function and dismiss function
 * 
 * @example
 * const { toast, dismiss } = useToast();
 * 
 * // Show success notification
 * const { id } = toast({ title: "Saved successfully" });
 * 
 * // Dismiss specific notification
 * dismiss(id);
 */
export function useToast() {
  return {
    toast,
    dismiss: (toastId?: string | number) => {
      if (toastId) {
        sonnerToast.dismiss(toastId);
      } else {
        sonnerToast.dismiss();
      }
    },
    // For backward compatibility, return empty toasts array
    // (Sonner manages its own state internally)
    toasts: [] as any[],
  };
}

// ============================================================================
// CONVENIENCE EXPORTS
// ============================================================================

// Export Sonner directly for new code that wants to use it directly
export { toast as sonnerToast } from "sonner";
